import {
	SearchInput,
	SearchPlaceholder,
	TabsNavigation,
	TabsNavigationItemData,
	Toolbar
} from "client/Components";
import { IObjectInputAttachable } from "client/Forms/attachable";
import {
	ReactElement,
	useMemo,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { HubItemComposerDialog } from "../../HubItemComposerDialog/HubItemComposerDialog";
import {
	AttendeeIdWithKind,
	EventSaveModel,
	EventStatus,
	LocationType
} from "client/Api/EventsManagement/eventsManagement";
import { UserGridItem } from "client/Api/UserManagement/userManagement";
import styles from "./AttendeesStep.module.less";
import {
	EmptyMessages,
	UserCollection,
	UserCollectionMode
} from "client/Features/Users/UserAndUserGroupCollection/UserCollection";
import { UserAndUserGroupKind } from "client/Features/Users/UserAndUserGroupCollection/UserAndUserGroupCollection";
import { partition } from "client_legacy/ndaw/arrayHelpers";
import { AttendeeCounter, EventAttendeeKind } from "client/Api/Hub/Events/eventViewer";
import { getAttendeeKindTabs } from "client/Features/Hub/Events/Attendees/AttendeesDialog";
import { NumberFormatter } from "client/Hooks";
import { isNil } from "client/Misc/utils";
import { useHubStatisticsFormatter } from "client/Features/Hub/useHubStatisticsFormatter";

const {
	Title,
	Step,
	Content,
	FormContainer
} = HubItemComposerDialog;

export type AttendeesStepProps = {
	isActive: boolean;
	attachable: IObjectInputAttachable<EventSaveModel>;
	status: EventStatus;
	attendeeIdsWithKind: readonly AttendeeIdWithKind[];
	allUsers: readonly UserGridItem[];
	onExport: (() => void) | undefined;
	infoAlerts?: ReactElement;
};

export const AttendeesStep = (props: AttendeesStepProps): ReactElement => {
	const {
		isActive,
		attachable,
		attendeeIdsWithKind,
		allUsers,
		status,
		infoAlerts,
		onExport
	} = props;

	const [searchTerm, setSearchTerm] = useState<string>("");
	const [activeTabId, setActiveTabId] = useState<EventAttendeeKind | nil>(null);

	const statisticsFormatter: NumberFormatter = useHubStatisticsFormatter();

	const isArchived: boolean = status === EventStatus.Archived;
	const locationType: LocationType | null = attachable.value
		.locationType;

	const removedAttendeeIds: readonly number[] = useMemo(
		() => {
			return attachable.for("removedAttendeeIds")
				.value;
		},
		[attachable]
	);

	const emptyMessages = useMemo(
		(): EmptyMessages=> {
			return {
				noSearchResult: {
					icon: "magnifying-glass",
					title: localize("_NoSearchResults"),
					message: localize("_PleaseTrySearchingWithAnotherTerm")
				},
				empty: {
					icon: "user",
					title: localize("_NoAttendeesYet"),
					message: status === EventStatus.Draft || status === EventStatus.Planned
						? localize("_FutureAttendeesCanBeManagedHere")
						: localize("_ThereAreCurrentlyNoAttendeesYet")
				}
			};
		},
		[status]
	);

	const userIdsWithKind = useMemo(
		(): readonly AttendeeIdsWithKind[] => {
			const ids: readonly AttendeeIdWithKind[] = attendeeIdsWithKind
				.filter(x=> !removedAttendeeIds.includes(x.id));

			return getUserIdsWithKind(ids);
		},
		[
			attendeeIdsWithKind,
			removedAttendeeIds
		]
	);

	const isHybrid: boolean = locationType === LocationType.Hybrid;

	const tabsData = useMemo(
		(): TabsNavigationItemData<EventAttendeeKind | undefined>[] => {
			if (!isHybrid) {
				return [];
			}

			const attendeeCounts: readonly AttendeeCounter[] = userIdsWithKind
				.filter(x => x.userIds.length > 0)
				.map(
					x => ({
						kind: x.kind,
						count: x.userIds.length
					})
				);

			return getAttendeeKindTabs(attendeeCounts, statisticsFormatter);
		},
		[
			isHybrid,
			statisticsFormatter,
			userIdsWithKind
		]
	);

	const activeTabIdToRender = useMemo(
		(): number | undefined => {
			if (tabsData.length === 0) {
				return;
			}

			return activeTabId === null || !tabsData.some(x => x.value === activeTabId)
				? tabsData[0].value
				: activeTabId;
		},
		[
			activeTabId,
			tabsData
		]
	);

	const userCollectionValue = useMemo(
		(): readonly number[] => {
			if (!isHybrid || (isHybrid && isNil(activeTabIdToRender))) {
				return userIdsWithKind.reduce(
					(ids: readonly number[], a) => [...ids, ...a.userIds],
					[]
				);
			}

			return userIdsWithKind
				.find(x => x.kind === activeTabIdToRender)
				?.userIds ?? [];
		},
		[
			activeTabIdToRender,
			isHybrid,
			userIdsWithKind
		]
	);

	const mode = useMemo(
		(): UserCollectionMode => {
			if (isArchived) {
				return {
					kind: UserAndUserGroupKind.Read
				};
			}

			return {
				kind: UserAndUserGroupKind.Edit,
				onChange: (userIds: readonly number[]): void => {
					const alreadyDeletedAttendeeIds: readonly number[] = attachable.for("removedAttendeeIds")
						.value;

					const newlyDeletedIds: readonly number[] = userCollectionValue.filter(
						x => !userIds.includes(x)
					);

					attachable.for("removedAttendeeIds")
						.reducer
						.setValue([...alreadyDeletedAttendeeIds, ...newlyDeletedIds]);
				}
			};
		},
		[
			attachable,
			isArchived,
			userCollectionValue
		]
	);

	return (
		<Step
			trackingId="attendees-step"
			isActive={isActive}
		>
			<Content stretchHeight>
				{infoAlerts}
				<FormContainer>
					<Title text={localize("_Attendees")}/>
				</FormContainer>

				{onExport && attendeeIdsWithKind.length > 0 && (
					<div className={styles.toolbar}>
						<Toolbar>
							<Toolbar.Button
								trackingId="export"
								text={localize("_ExportAllToCsv")}
								kind="secondary"
								icon="file-export"
								onClick={onExport}
							/>
						</Toolbar>
					</div>
				)}

				{isHybrid && tabsData.length > 0 && (
					<div className={styles.tabs}>
						<TabsNavigation
							value={activeTabIdToRender}
							items={tabsData}
							onChange={setActiveTabId}
							trackingId="attendee-kind-tabs"
						/>
					</div>
				)}
				<div className={styles.searchContainer}>
					<SearchInput
						trackingId="search"
						onChange={setSearchTerm}
						icon="filter"
						height="large"
						placeholder={(
							<SearchPlaceholder
								kind="filter"
								properties={[
									localize("_Name"),
									localize("_Email"),
									localize("_Username")
								]}
							/>
						)}
					/>
				</div>
				<UserCollection
					height="fill"
					allData={allUsers}
					mode={mode}
					searchTerm={searchTerm}
					value={userCollectionValue}
					emptyMessages={emptyMessages}
				/>
			</Content>
		</Step>
	);
};
AttendeesStep.displayName = "AttendeesStep";

type AttendeeIdsWithKind = {
	kind: EventAttendeeKind;
	userIds: readonly number[];
};

function getUserIdsWithKind(
	attendeeIdsWithKind: readonly AttendeeIdWithKind[]
): readonly AttendeeIdsWithKind[] {
	const [
		onlineAttendees,
		physicallyAttendees
	] = partition(
		attendeeIdsWithKind,
		x => x.kind === EventAttendeeKind.Online
	);

	return [
		{
			kind: EventAttendeeKind.Online,
			userIds: onlineAttendees.map(x => x.id)
		},
		{
			kind: EventAttendeeKind.Physical,
			userIds: physicallyAttendees.map(x => x.id)
		}
	];
}